import { type RefObject, useEffect } from 'react'

/**
 * Measures the height of the top chrome area (banner + navigation) and publishes it
 * as a CSS variable (--app-top-offset) for consumption by fixed-position Pattern UI components.
 *
 * @param topChromeRef - Ref to the container wrapping top chrome elements
 * @param dependencies - Values that should trigger re-measurement when changed (e.g., appEnv, workspace count)
 *
 */
export function useTopChromeOffset(
  topChromeRef: RefObject<HTMLDivElement>,
  dependencies: React.DependencyList = [],
) {
  useEffect(() => {
    if (typeof document === 'undefined') return

    const topChrome = topChromeRef.current
    if (!topChrome) return

    let lastOffset = -1
    const updateTopOffset = () => {
      const topOffset = Math.round(topChrome.getBoundingClientRect().height)
      if (topOffset === lastOffset) return
      lastOffset = topOffset
      document.documentElement.style.setProperty(
        '--app-top-offset',
        `${topOffset}px`,
      )
    }

    updateTopOffset()

    const observer = new ResizeObserver(updateTopOffset)
    observer.observe(topChrome)

    return () => {
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
}
